/**
 * THE recovery journal — the ONE authority for a project's backup copies of its drawing
 * (snapshots-p2a-s10, D1; CTO 65-62 ring, 65-69 (1) one key predicate, 65-81/65-82 seed and
 * tier move, 65-85 FR-P2, 65-104 CAP = 10).
 *
 * P-RING: when a save would replace a stored drawing that holds anything with an EMPTY drawing,
 * the stored record is kept as a backup copy in the SAME all-or-nothing storage step; if it
 * cannot be kept, the save does not happen and the user is told. Copies leave only by the
 * oldest-past-CAP eviction (at most two per add), by project delete, or by the ONE proven
 * localStorage tier move. No rule compares two records' MEANING to decide what to keep.
 *
 * This module owns, once: the CAP; the slot keys and `isRecoverySlotKey` (shared by the ring,
 * the boot scan exclusion, the delete refusal and the censuses); the envelope; `legacyBytes`
 * (the ONE legacy identity); the incoming-empty arming predicate; `armsRecovery`; and the two
 * PURE planners. The transaction owner (`indexed-db.ts`) runs them; nothing restates them.
 *
 * ⛔ A LEAF: it imports only `documentWriteGuard.ts` (itself a leaf). No module-scope work
 *    (WR-682 / BOOT-TDZ-001). No storage access — every function here is pure.
 *
 * @see architecture/Snapshots/port-p2a-s9/stage4-proposal.md §1 D1 (rev 3a)
 * @see src/core/storage/__tests__/recoveryJournal.test.ts
 */
import { projectDrawingKey, sameDrawing } from "./documentWriteGuard";

/** How many backup copies a project keeps (PM, CTO 65-104: "10 copies"). */
export const RECOVERY_RING_CAP = 10;

/**
 * At most this many evictions per added copy: above CAP the ring shrinks by one net per add
 * (converges to CAP, never mass-deletes after a CAP is lowered — R1c2-F5).
 */
export const RECOVERY_EVICTIONS_PER_ADD = 2;

const SEQ_DIGITS = 10;
const MAX_SEQ = 10 ** SEQ_DIGITS - 1;
const LEGACY_SUFFIX = "_preempty";
/** `project_<id>_state_preempty` optionally followed by `_` + exactly 10 digits. */
const SLOT_KEY = /^project_(.+)_state_preempty(?:_(\d{10}))?$/;

/** Today's single-slot key `project_<id>_state_preempty` (a legacy slot; never written by this build). */
export function legacyRecoverySlotKey(projectId: string): string {
  return `${projectDrawingKey(projectId)}${LEGACY_SUFFIX}`;
}

/** The ring's key prefix — the legacy key + `_` (the trailing `_` keeps the legacy key out of the range). */
export function recoveryRingPrefix(projectId: string): string {
  return `${legacyRecoverySlotKey(projectId)}_`;
}

/** A ring slot's key: prefix + the sequence zero-padded to 10 digits (lexical order = journal order). */
export function recoveryRingKey(projectId: string, seq: number): string {
  if (!Number.isInteger(seq) || seq < 1 || seq > MAX_SEQ) {
    throw new RangeError(`[RECOVERY-JOURNAL] invalid sequence ${seq}`);
  }
  return `${recoveryRingPrefix(projectId)}${String(seq).padStart(SEQ_DIGITS, "0")}`;
}

/** The inclusive key bounds of a project's ring (for `IDBKeyRange.bound`). */
export function recoveryRingBounds(projectId: string): { lower: string; upper: string } {
  const p = recoveryRingPrefix(projectId);
  return { lower: `${p}${"0".repeat(SEQ_DIGITS)}`, upper: `${p}${"9".repeat(SEQ_DIGITS)}` };
}

/**
 * THE key predicate (CTO 65-69 (1)): a legacy slot key OR a ring slot key. Every reader that
 * must tell a backup copy from other records asks this — never a prefix test of its own.
 */
export function isRecoverySlotKey(key: unknown): key is string {
  return typeof key === "string" && SLOT_KEY.test(key);
}

/** A ring slot key (a recovery slot key carrying a sequence). */
export function isRecoveryRingKey(key: unknown): key is string {
  if (typeof key !== "string") return false;
  const m = SLOT_KEY.exec(key);
  return !!m && m[2] !== undefined;
}

/** The sequence of a ring slot key, else null. */
export function recoveryRingSeq(key: string): number | null {
  const m = SLOT_KEY.exec(key);
  return m && m[2] !== undefined ? Number(m[2]) : null;
}

/**
 * THE envelope — the ONE shape of a ring slot. `record` is the stored drawing exactly as it
 * was (an object, or a string when the stored bytes were a string). `journaledAt` is null ONLY
 * for a copy migrated from a legacy slot, which then names the tier it came from.
 */
export type RecoveryEnvelope =
  | { ring: 1; seq: number; journaledAt: number; record: unknown; migratedFrom?: undefined }
  | { ring: 1; seq: number; journaledAt: null; record: unknown; migratedFrom: "idb" | "ls" };

export function isRecoveryEnvelope(v: unknown): v is RecoveryEnvelope {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const e = v as Record<string, unknown>;
  if (e.ring !== 1 || !Number.isInteger(e.seq) || (e.seq as number) < 1 || !("record" in e)) return false;
  if (e.journaledAt === null) return e.migratedFrom === "idb" || e.migratedFrom === "ls";
  return typeof e.journaledAt === "number" && e.migratedFrom === undefined;
}

/**
 * THE legacy byte identity (FR-P3/P4): a string is its own bytes, anything else its JSON.
 * Used for EVERY legacy-slot decision — seed divergence and the tier move — and nothing else.
 */
export function legacyBytes(v: unknown): string {
  return typeof v === "string" ? v : JSON.stringify(v);
}

/**
 * True iff an INCOMING drawing carries zero spaces AND zero pages (absent/malformed scenes ⇒
 * empty). A `{ data }` envelope is unwrapped. The INCOMING side only — the stored side is read
 * by `storedManifestIsPositivelyEmpty` (documentVersions.ts). Moved from
 * `ElementStorage.manifestScenesAreEmpty` (s10 D1).
 */
export function incomingIsEmptyDrawing(manifest: unknown): boolean {
  const outer = manifest && typeof manifest === "object" ? (manifest as Record<string, unknown>) : null;
  const m =
    outer && "data" in outer && outer.data && typeof outer.data === "object"
      ? (outer.data as Record<string, unknown>)
      : outer;
  const scenes = m ? (m as { scenes?: { spaces?: unknown; pages?: unknown } }).scenes : undefined;
  const count = (v: unknown) => (typeof v === "object" && v !== null ? Object.keys(v).length : 0);
  return !scenes || typeof scenes !== "object" || (count(scenes.spaces) === 0 && count(scenes.pages) === 0);
}

/**
 * Whether a write MUST carry its stored record into the ring (RG-1: arming is by KEY, decided
 * here, once): the exact drawing key of the declared project, with an empty incoming drawing.
 */
export function armsRecovery(key: string, data: unknown, projectId: string | undefined): boolean {
  return !!projectId && key === projectDrawingKey(projectId) && incomingIsEmptyDrawing(data);
}

export interface RecoveryAdd {
  key: string;
  value: RecoveryEnvelope;
}

function ringSeqs(projectId: string, ringKeys: readonly unknown[]): number[] {
  const prefix = recoveryRingPrefix(projectId);
  const seqs: number[] = [];
  for (const k of ringKeys) {
    if (isRecoveryRingKey(k) && k.startsWith(prefix)) {
      const s = recoveryRingSeq(k);
      if (s !== null && k === recoveryRingKey(projectId, s)) seqs.push(s);
    }
  }
  return seqs.sort((a, b) => a - b);
}

/**
 * The ONE seed planner (LD-7; eager at boot and lazily inside an armed write — both call this,
 * so both produce the byte-identical ring from the same legacy input). Empty unless the ring is
 * empty and a legacy value exists. The LS raw string is parsed first (an unparseable one is kept
 * as its raw string). LS DIVERGES from IDB iff their `legacyBytes` differ; a diverging LS copy is
 * the OLDER (seq 1) and IDB seq 2; otherwise the one present copy is seq 1.
 */
export function planRecoverySeed(input: {
  projectId: string;
  ringKeys: readonly unknown[];
  legacyIdb: unknown;
  legacyLsRaw: string | null;
}): RecoveryAdd[] {
  const { projectId, ringKeys, legacyIdb, legacyLsRaw } = input;
  if (ringSeqs(projectId, ringKeys).length > 0) return [];
  const hasIdb = legacyIdb !== undefined && legacyIdb !== null;
  const hasLs = legacyLsRaw !== null && legacyLsRaw !== undefined;
  const sources: Array<{ from: "idb" | "ls"; record: unknown }> = [];
  if (hasLs && (!hasIdb || legacyBytes(legacyIdb) !== legacyLsRaw)) {
    let record: unknown = legacyLsRaw;
    try {
      record = JSON.parse(legacyLsRaw);
    } catch {
      // raw bytes: kept as the string
    }
    sources.push({ from: "ls", record });
  }
  if (hasIdb) sources.push({ from: "idb", record: legacyIdb });
  return sources.map((s, i) => ({
    key: recoveryRingKey(projectId, i + 1),
    value: { ring: 1, seq: i + 1, journaledAt: null, record: s.record, migratedFrom: s.from },
  }));
}

/**
 * The ONE add planner (LD-1, LD-3). Inputs are the POST-seed ring keys and the POST-seed newest
 * slot (R1c2-F6). A candidate `sameDrawing`-identical to the newest slot's record adds nothing;
 * otherwise it is added at max seq + 1 and the LOWEST sequences past `cap` are evicted, at most
 * `RECOVERY_EVICTIONS_PER_ADD` of them. Only THIS project's ring keys are ever planned against.
 */
export function planRecoveryAdd(input: {
  projectId: string;
  ringKeys: readonly unknown[];
  newest: unknown;
  candidate: unknown;
  now: number;
  cap: number;
}): { add?: RecoveryAdd; evict: string[] } {
  const { projectId, ringKeys, newest, candidate, now, cap } = input;
  if (isRecoveryEnvelope(newest) && sameDrawing(candidate, newest.record)) {
    return { evict: [] };
  }
  const seqs = ringSeqs(projectId, ringKeys);
  const seq = (seqs.length ? seqs[seqs.length - 1] : 0) + 1;
  const add: RecoveryAdd = {
    key: recoveryRingKey(projectId, seq),
    value: { ring: 1, seq, journaledAt: now, record: candidate },
  };
  const excess = seqs.length + 1 - cap;
  const evictCount = Math.min(Math.max(excess, 0), RECOVERY_EVICTIONS_PER_ADD, seqs.length);
  return { add, evict: seqs.slice(0, evictCount).map((s) => recoveryRingKey(projectId, s)) };
}

/**
 * The tier move's proof (CTO 65-82, the ONE exception to add-only; 65-85 FR-P2; FR-R2): the LS
 * legacy value — read AGAIN by the caller immediately before its delete — PARSES, and a MIGRATED
 * slot read back in this pass holds exactly its bytes. Anything else keeps the LS copy.
 */
export function lsLegacyMoveIsProven(lsRawNow: string | null, readBack: readonly unknown[]): boolean {
  if (typeof lsRawNow !== "string") return false;
  try {
    JSON.parse(lsRawNow);
  } catch {
    return false;
  }
  return readBack.some(
    (s) => isRecoveryEnvelope(s) && s.migratedFrom !== undefined && legacyBytes(s.record) === lsRawNow,
  );
}
